// /work-orders/:id: one run. The header carries the act the run is waiting
// for (approve, release, resume, close); the body is whether Assembly covers
// the run and the units, with the history beside them on a wide screen.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import PageLayout from '../components/ui/PageLayout';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { formatEventDate } from '../utils/date-utils';
import { variantLabel } from '../Parts/bomsApi';
import { buttonClass } from '../components/ui/Button';
import CloseReportSection from './CloseReportSection';
import DismissDialog from './DismissDialog';
import EventsThread from './EventsThread';
import JourneyStrip from './JourneyStrip';
import NewWorkOrderDrawer from './NewWorkOrderDrawer';
import CoverageCard from './CoverageCard';
import UnitsSection from './UnitsSection';
import { MetaChip, StatusChip } from './WorkOrderBits';
import { modelReading, useWorkOrdersApi, workOrderErrorMessage, type WorkOrderDetail } from './workOrdersApi';

// The detail no longer carries parts list lines, so a legacy shortfall reads by its target key.
const NO_LINE_NAMES: Record<number, string> = {};

const LINK = 'rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

const HELP = (
  <>
    <p>
      A work order is one run of drones of one model. It is suggested by a confirmed sale or raised by hand,
      approved by a manager, then released to the floor. Releasing fixes the parts list. Drones join and finish
      the run by scan, in Assembly mode on the scanner.
    </p>
    <p className="font-medium text-on-surface">Parts</p>
    <p>
      Whether Assembly holds what the run still needs. Assembly is one pool shared by every approved run, and
      older runs draw from it first, so the card reads covered, or short by what is left for this run. A run on
      hold is not counted. Boxes are picked on the pick list, for every run at once.
    </p>
    <p>
      When a drone at the station comes up short, the station asks for a box. The request waits at the top of the
      pick list until someone picks a box holding that part, which fills it.
    </p>
    <p className="font-medium text-on-surface">Units</p>
    <p>
      One row per drone. A drone joins when Assembly mode scans it and is built when its final check passes. A
      short part never finishes a drone: pick another box, or hold the run. A drone that fails at the bench is
      flagged off the run carrying a full set of parts, charged to the run when it was flagged, and the How column says why.
    </p>
    <p className="font-medium text-on-surface">Close report</p>
    <p>Once every drone is built, the report sets what the run used against what its parts list called for.</p>
    <p className="font-medium text-on-surface">Closed or cancelled</p>
    <p>
      Both end a run, but they mean different things. Closed is the normal end: the run was built, fully or
      short, and the close report is its account. Leftover boxes stay in Assembly for the next run. Cancelled
      means the run was called off: a suggestion dismissed, or a draft or released run stopped, with a reason.
      Nothing already done is undone, so a box already picked stays in Assembly and a drone already built keeps
      its cost, but there is no report. Closed runs sit under Done; cancelled ones show only under All.
    </p>
  </>
);

const WorkOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const woApi = useWorkOrdersApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const canWrite = isAdmin || !!user?.can_manufacturing;
  const canManage = isAdmin || !!user?.can_manager;

  const [detail, setDetail] = useState<WorkOrderDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // The order on screen. A slow answer for the order navigated away from must not land on this one.
  const currentId = useRef(id);
  const hasDetail = useRef(false);
  hasDetail.current = detail != null;

  const load = useCallback(async () => {
    try {
      const next = await woApi.get(id);
      if (currentId.current !== id) return;
      setDetail(next);
      setLoadError(null);
    } catch (err) {
      if (currentId.current !== id) return;
      const message = workOrderErrorMessage(err, 'This work order did not load.');
      setLoadError(message);
      if (hasDetail.current) toast.error(`${message} What is shown may be out of date.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    currentId.current = id;
    hasDetail.current = false;
    setDetail(null);
    load();
  }, [load, id]);

  /** Runs one header action, replaces the page with what the server returned. */
  const act = async (run: () => Promise<WorkOrderDetail>, done: string, failed: string): Promise<boolean> => {
    setBusy(true);
    try {
      setDetail(await run());
      toast.success(done);
      return true;
    } catch (err) {
      toast.error(workOrderErrorMessage(err, failed));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !detail) {
    return (
      <PageLayout width="wide" kind="record" className="pt-4">
        <Alert
          tone="error"
          onRetry={load}
          action={
            <Link to="/work-orders" className={buttonClass('quiet', 'md')}>
              All work orders
            </Link>
          }
        >
          {loadError}
        </Alert>
      </PageLayout>
    );
  }

  if (!detail) {
    return (
      <PageLayout width="wide" kind="record" className="pt-4">
        <LogoSpinner label="Loading the work order" />
      </PageLayout>
    );
  }

  const o = detail.order;
  const s = o.status;
  const running = s === 'released' || s === 'in_progress';
  const finished = s === 'closed' || s === 'cancelled';
  const unitsToGo = o.units_to_go ?? Math.max(0, o.quantity - detail.units.length);

  const lede = [
    modelReading(o),
    o.variant ? `${variantLabel(o.variant)} build` : null,
  ]
    .filter(Boolean)
    .join(', ');

  // The one filled act is whatever the run is waiting for.
  let primary: React.ReactNode;
  if (s === 'suggested' && canManage) {
    primary = (
      <PrimaryAction
        disabled={busy}
        onClick={() => act(() => woApi.approve(o.id), `Approved ${o.number}. Release it when the floor is ready.`, 'The approval did not save. Try again.')}
      >
        Approve
      </PrimaryAction>
    );
  } else if (s === 'draft' && canWrite) {
    primary = (
      <PrimaryAction
        disabled={busy}
        onClick={() => act(() => woApi.release(o.id), `Released ${o.number} to the floor.`, 'The work order was not released. Try again.')}
      >
        Release
      </PrimaryAction>
    );
  } else if (s === 'on_hold' && canManage) {
    primary = (
      <PrimaryAction
        disabled={busy}
        onClick={() => act(() => woApi.resume(o.id), `Resumed ${o.number}.`, 'The work order did not resume. Try again.')}
      >
        Resume
      </PrimaryAction>
    );
  } else if (s === 'complete' && canManage) {
    primary = (
      <DismissDialog
        triggerLabel="Close"
        triggerClassName={buttonClass('filled', 'md')}
        tooltip="Close the run. The close report is final from then on."
        title={`Close ${o.number}`}
        fieldLabel="Anything to note? (optional)"
        confirmLabel="Close work order"
        required={false}
        tone="primary"
        align="right"
        onConfirm={(note) => act(() => woApi.close(o.id, note || undefined), `Closed ${o.number}.`, 'The work order did not close. Try again.')}
      />
    );
  }

  const actions = (
    <>
      {(s === 'suggested' || s === 'draft') && canWrite && (
        <SecondaryAction onClick={() => setEditing(true)} disabled={busy}>
          Edit
        </SecondaryAction>
      )}
      {running && canManage && (
        <>
          <DismissDialog
            triggerLabel="Hold"
            triggerClassName={buttonClass('tonal', 'md')}
            tooltip="Pause the run. Nothing can be picked or built until it resumes."
            title={`Put ${o.number} on hold`}
            fieldLabel="Why is it paused? (optional)"
            confirmLabel="Put on hold"
            required={false}
            tone="primary"
            align="right"
            onConfirm={(reason) => act(() => woApi.hold(o.id, reason || undefined), `${o.number} is on hold.`, 'The work order was not put on hold. Try again.')}
          />
          <DismissDialog
            triggerLabel="Close"
            triggerClassName={buttonClass('tonal', 'md')}
            tooltip="Close the run now. Drones not yet built show as short on the close report."
            title={`Close ${o.number} early`}
            fieldLabel="Why close before every drone is built? (optional)"
            confirmLabel="Close work order"
            required={false}
            tone="primary"
            align="right"
            onConfirm={(note) => act(() => woApi.close(o.id, note || undefined), `Closed ${o.number}.`, 'The work order did not close. Try again.')}
          />
        </>
      )}
      {s === 'suggested' && canManage && (
        <DismissDialog
          triggerClassName={buttonClass('danger', 'md')}
          tooltip="Not needed. Cancels the suggestion with your reason."
          align="right"
          onConfirm={(reason) => act(() => woApi.cancel(o.id, reason), `Dismissed ${o.number}.`, 'The suggestion was not dismissed. Try again.')}
        />
      )}
      {(s === 'draft' || running || s === 'on_hold') && canManage && (
        <DismissDialog
          triggerLabel="Cancel work order"
          triggerClassName={buttonClass('danger', 'md')}
          tooltip="Stop this run for good. Parts already in Assembly stay there."
          title={`Cancel ${o.number}`}
          fieldLabel="Why is it cancelled?"
          confirmLabel="Cancel work order"
          align="right"
          onConfirm={(reason) => act(() => woApi.cancel(o.id, reason), `Cancelled ${o.number}.`, 'The work order was not cancelled. Try again.')}
        />
      )}
      {o.sales_order_id != null && s !== 'cancelled' && (
        <SecondaryAction variant="text" to={`/orders/${o.sales_order_id}?ship=1`}>
          Ship on {o.so_number || 'the sales order'}
        </SecondaryAction>
      )}
      <SecondaryAction variant="text" to="/work-orders">
        All work orders
      </SecondaryAction>
    </>
  );

  const meta = (
    <>
      <StatusChip status={s} />
      <MetaChip>
        <span className="tabular-nums">{o.quantity}</span> {o.quantity === 1 ? 'drone' : 'drones'}
      </MetaChip>
      <Appear show={!!o.planned_date}>
        <MetaChip>Planned {o.planned_date ? formatEventDate(o.planned_date) : ''}</MetaChip>
      </Appear>
      {o.sales_order_id != null && (
        <MetaChip>
          For{' '}
          <Link to={`/orders/${o.sales_order_id}`} className={`text-on-surface ${LINK}`}>
            {o.so_number || 'a sales order'}
          </Link>
        </MetaChip>
      )}
    </>
  );

  const released = !!o.released_at;
  const coverageKey = `${s}-${o.completed_count}-${detail.events.length}`;
  const showReport = s === 'complete' || s === 'closed';

  return (
    <PageLayout width="wide"
      kind="record"
      className="space-y-5 pt-4"
      header={
        <>
          <PageHeader title={o.number} helpTitle="About a work order" lede={lede} help={HELP} meta={meta} primary={primary} actions={actions} />

          <JourneyStrip order={o} />

          <Reveal show={!!o.notes}>
            <p className="max-w-3xl whitespace-pre-line break-words text-sm text-on-surface-variant">{o.notes}</p>
          </Reveal>
        </>
      }
      aside={<EventsThread events={detail.events} />}
    >
      <FadeIn key={s === 'draft' ? 'draft' : !released ? 'waiting' : 'live'} className="min-w-0 space-y-5">
          {s === 'draft' ? (
            <CoverageCard orderId={o.id} orderStatus={s} refreshKey={coverageKey} />
          ) : !released ? (
            <>
              <EmptyState title="Not on the floor yet">
                {s === 'suggested'
                  ? 'A manager approves this suggestion first. Its parts join the pick list then, and drones join once it is released.'
                  : 'This work order was cancelled before it was released.'}
              </EmptyState>
              {s === 'suggested' && <CoverageCard orderId={o.id} orderStatus={s} refreshKey={coverageKey} />}
            </>
          ) : (
            <>
              <Reveal show={!finished && s !== 'complete'}>
                <CoverageCard orderId={o.id} orderStatus={s} refreshKey={coverageKey} />
              </Reveal>
              <UnitsSection
                orderId={o.id}
                units={detail.units}
                lineNames={NO_LINE_NAMES}
                quantity={o.quantity}
                unitsToGo={unitsToGo}
                canRemove={!finished && canWrite}
                onChanged={load}
              />
              <Reveal show={showReport}>
                <CloseReportSection orderId={o.id} refreshKey={`${s}-${o.completed_count}-${o.closed_at ?? ''}`} />
              </Reveal>
            </>
          )}
      </FadeIn>

      {editing && <NewWorkOrderDrawer editing={o} onClose={() => setEditing(false)} onSaved={setDetail} />}
    </PageLayout>
  );
};

export default WorkOrderPage;
